import { Icon } from "@blueprintjs/core";

import { Box, Flex } from "@/components/layout/flexbox";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { AIStarSvg } from "@/components/pieces/roleplays/svg/StarSvg";
import { AvatarWithPrettyNull } from "@/components/pieces/users/AvatarWithPrettyNull";
import { LIGHT_BLUE, MIDTEXT_COLOR, SUBTEXT_COLOR } from "@/css/constants";
import { RoleplaySessionTranscriptFragment } from "@/graphql";
import { formatMinutes } from "@/utils/datetime";

interface ChatMessageProps extends RoleplaySessionTranscriptFragment {
  sender?: {
    name: string;
    photo: string;
  };
  secondsFromStart?: number | null;
  nextMessageStart?: number | null;
  seekTime?: number;
  duration?: number | null;
  isFeedbackActive: boolean;
  onClickItem?: () => void;
  onClickMessage?: () => void;
}

export function ChatMessage({
  sender,
  feedbacks = [],
  secondsFromStart,
  nextMessageStart,
  seekTime = 0,
  duration,
  text,
  isFeedbackActive,
  onClickItem,
  onClickMessage,
  aiComments = [],
}: ChatMessageProps) {
  const isActive = nextMessageStart
    ? seekTime >= (secondsFromStart ?? 0) && seekTime < nextMessageStart
    : seekTime >= (secondsFromStart ?? 0) && seekTime < (duration ?? 0) - 0.5;

  const canClick = (feedbacks?.length ?? 0) > 0 && onClickMessage;

  return (
    <Clickable
      onClick={canClick ? onClickMessage : undefined}
      onFocus={() => {}}
    >
      <Flex
        css={{
          background: isFeedbackActive
            ? LIGHT_BLUE
            : isActive
              ? "#FFFBF2"
              : "transparent",
          borderRadius: 6,
          cursor: canClick ? "pointer" : undefined,
          transition: canClick ? "background-color 0.2s ease" : undefined,
          border: "1px solid transparent",
          "&:hover": {
            backgroundColor: canClick ? "#F6FAFF" : undefined,
            border: isFeedbackActive ? `1px solid  ${LIGHT_BLUE}` : undefined,
          },
        }}
        p={2}
        mb={1}
      >
        <AvatarWithPrettyNull
          user={{
            name: sender?.name,
            photo: sender?.photo,
          }}
          size={22}
        />
        <Flex flex={1} flexDirection="column" ml={2} lineHeight="24px">
          <Flex mb={1} justifyContent="space-between">
            <Box fontWeight={600} fontSize={16}>
              {sender?.name}
              <Box
                as="a"
                fontSize={12}
                ml={2}
                fontWeight={400}
                color={MIDTEXT_COLOR}
                css={{ display: "inline" }}
                onClick={onClickItem}
              >
                {formatMinutes(secondsFromStart)}
              </Box>
            </Box>
            {!!feedbacks?.length && onClickMessage && (
              <Flex
                alignItems="center"
                style={{
                  gap: "8px",
                }}
              >
                {!!aiComments?.length && <AIStarSvg width={20} height={20} />}
                <Icon icon="comment" color={SUBTEXT_COLOR} size={12} />
              </Flex>
            )}
          </Flex>
          <Box fontSize={16}>{text}</Box>
        </Flex>
      </Flex>
    </Clickable>
  );
}
